import Decimal from "decimal.js";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import type { JournalLine, Transaction } from "@/apis/gql/types";
import { Shortcut } from "@/components/ui/shortcut";

import { ConfirmBankTransactionBookingsButton } from "./confirm-bank-transaction-bookings-button";
import { NavigationElement, useNavigationContext } from "./navigation";

type Props = {
  transactions: Transaction[];
  journalLines: JournalLine[];
  currency: string;
  onConfirmBookings?: (bankTxnIds?: string[]) => Promise<void>;
};

export const BankBookingsFooter = ({
  transactions,
  journalLines,
  currency = "EUR",
  onConfirmBookings,
}: Props) => {
  const { t } = useTranslation();
  const { isLastInput } = useNavigationContext();

  const totalDebit = useMemo(() => {
    return journalLines.reduce(
      (acc, line) => new Decimal(acc).plus(line.debit ?? 0),
      new Decimal(0),
    );
  }, [journalLines]);

  const totalCredit = useMemo(() => {
    return journalLines.reduce(
      (acc, line) => new Decimal(acc).plus(line.credit ?? 0),
      new Decimal(0),
    );
  }, [journalLines]);

  const amount = useMemo(() => {
    return new Decimal(totalDebit).minus(totalCredit).toNumber();
  }, [totalDebit, totalCredit]);

  const isZero = amount === 0;

  const isDisabled = useMemo(() => {
    if (isZero) {
      return false;
    }

    return true;
  }, [isZero]);

  const handleConfirmTransactionBooking = async () => {
    await onConfirmBookings?.(
      transactions.map((transaction) => transaction.bankTxnId),
    );
  };

  if (!onConfirmBookings) {
    return null;
  }

  return (
    <div className="sticky bottom-0 flex border-t px-4 py-4 items-center shadow-[0_-2px_4px_-1px_rgba(0,0,0,0.06)]">
      <div className="flex flex-row gap-2 w-full">
        <div className="flex-1 flex flex-row gap-4 items-center" />
        {isLastInput && (
          <div className="flex items-center gap-2 text-xs animate-in fade-in slide-in-from-left-1 duration-200">
            <Shortcut.ShortcutRoot>
              <div className="flex items-center gap-1 bg-muted rounded px-1.5 py-1">
                <Shortcut.ShortcutEnter className="bg-transparent p-0" />
                <span className="text-[10px] leading-none">Enter</span>
              </div>
            </Shortcut.ShortcutRoot>
            <span className="text-gray-500">
              {t("transactions.bank_bookings.shortcut_hint")}
            </span>
          </div>
        )}
        <NavigationElement>
          <ConfirmBankTransactionBookingsButton
            disabled={isDisabled}
            onClick={handleConfirmTransactionBooking}
          />
        </NavigationElement>
      </div>
    </div>
  );
};
